import React, { useCallback, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { Link, useParams } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';
import ResultatAprenentatgeModal from '../components/ResultatAprenentatgeModal';
import {
  Modul,
  ResultatAprenentatge,
  ResultatAprenentatgeForm,
  modulsService,
  resultatsAprenentatgeService,
} from '../services/api';

function TextRA({ valor }: { valor: string | null }) {
  return valor ? <span className="ra-long-text">{valor}</span> : <span className="text-muted">No informat</span>;
}

function ModulDetailPage() {
  const params = useParams<{ cicleId: string; modulId: string }>();
  const cicleId = Number(params.cicleId);
  const modulId = Number(params.modulId);
  const idsValids = Number.isInteger(cicleId) && cicleId > 0 && Number.isInteger(modulId) && modulId > 0;

  const [modul, setModul] = useState<Modul | null>(null);
  const [resultats, setResultats] = useState<ResultatAprenentatge[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorCarrega, setErrorCarrega] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [resultatToEdit, setResultatToEdit] = useState<ResultatAprenentatge | null>(null);
  const [resultatToDelete, setResultatToDelete] = useState<ResultatAprenentatge | null>(null);

  const carregarDades = useCallback(async () => {
    if (!idsValids) {
      setErrorCarrega('El mòdul professional indicat no és vàlid.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setErrorCarrega('');
      const [modulData, resultatsData] = await Promise.all([
        modulsService.getById(cicleId, modulId),
        resultatsAprenentatgeService.getAll(cicleId, modulId),
      ]);
      setModul(modulData);
      setResultats(resultatsData);
    } catch (error) {
      console.error('Error carregant el detall del mòdul:', error);
      setErrorCarrega("No s'ha pogut carregar el mòdul professional.");
    } finally {
      setLoading(false);
    }
  }, [cicleId, modulId, idsValids]);

  useEffect(() => {
    carregarDades();
  }, [carregarDades]);

  const handleCreate = () => {
    setResultatToEdit(null);
    setModalOpen(true);
  };

  const handleEdit = (resultat: ResultatAprenentatge) => {
    setResultatToEdit(resultat);
    setModalOpen(true);
  };

  const handleSave = async (data: ResultatAprenentatgeForm) => {
    try {
      if (resultatToEdit) {
        await resultatsAprenentatgeService.update(cicleId, modulId, resultatToEdit.id, data);
      } else {
        await resultatsAprenentatgeService.create(cicleId, modulId, data);
      }
      setModalOpen(false);
      setResultatToEdit(null);
      await carregarDades();
    } catch (error) {
      console.error("Error guardant el resultat d'aprenentatge:", error);
      alert("No s'ha pogut guardar el resultat d'aprenentatge.");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!resultatToDelete) return;

    try {
      await resultatsAprenentatgeService.delete(cicleId, modulId, resultatToDelete.id);
      setResultatToDelete(null);
      await carregarDades();
    } catch (error) {
      console.error("Error eliminant el resultat d'aprenentatge:", error);
      alert("No s'ha pogut eliminar el resultat d'aprenentatge.");
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;

    const items = Array.from(resultats);
    const [resultatMogut] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, resultatMogut);
    setResultats(items);

    try {
      await resultatsAprenentatgeService.reorder(cicleId, modulId, items);
    } catch (error) {
      console.error("Error reordenant els resultats d'aprenentatge:", error);
      await carregarDades();
    }
  };

  if (loading) {
    return <div className="page-container"><div className="loading">Carregant...</div></div>;
  }

  if (errorCarrega || !modul) {
    return (
      <div className="page-container">
        <Link className="back-link" to={`/moduls?cicle=${cicleId}`}>← Tornar als mòduls</Link>
        <div className="error-state">{errorCarrega || "No s'ha trobat el mòdul professional."}</div>
      </div>
    );
  }

  return (
    <div className="page-container module-detail-page">
      <Link className="back-link" to={`/moduls?cicle=${cicleId}`}>← Tornar als mòduls</Link>

      <header className="module-detail-header">
        <div>
          <span className="module-detail-code">{modul.codi}</span>
          <h1>{modul.nom}</h1>
        </div>
        <div className="module-detail-hours">
          <span>Durada del mòdul</span>
          <strong>{modul.hores} h</strong>
        </div>
      </header>

      <div className="table-container ra-table-container">
        <div className="table-header">
          <div>
            <h2>Resultats d'aprenentatge</h2>
            <p className="table-subtitle">RA associats a aquest mòdul professional</p>
          </div>
          <button className="btn btn-primary" onClick={handleCreate}>+ Nou RA</button>
        </div>

        {resultats.length === 0 ? (
          <div className="empty-state">
            <p>Aquest mòdul encara no té resultats d'aprenentatge.</p>
            <button className="btn btn-primary empty-state-action" onClick={handleCreate}>Crear el primer RA</button>
          </div>
        ) : (
          <div className="table-scroll">
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="resultats-aprenentatge-list">
                {(provided) => (
                  <table className="ra-table" ref={provided.innerRef} {...provided.droppableProps}>
                    <thead>
                      <tr>
                        <th aria-label="Ordenar"></th>
                        <th>Codi</th>
                        <th>Nom</th>
                        <th>Descripció</th>
                        <th>Hores</th>
                        <th>Accions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultats.map((resultat, index) => (
                        <Draggable key={resultat.id} draggableId={`ra-${resultat.id}`} index={index}>
                          {(provided, snapshot) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={snapshot.isDragging ? 'dragging' : ''}
                            >
                              <td {...provided.dragHandleProps} className="drag-handle-cell">
                                <span className="drag-handle">⠿</span>
                              </td>
                              <td><code className="modul-codi">{resultat.codi}</code></td>
                              <td><strong>{resultat.nom}</strong></td>
                              <td><TextRA valor={resultat.descripcio} /></td>
                              <td>{resultat.hores ? <span className="modul-hores">{resultat.hores} h</span> : <span className="text-muted">No informades</span>}</td>
                              <td>
                                <div className="actions-cell">
                                  <button className="btn-icon edit" title="Editar" onClick={() => handleEdit(resultat)}>✏️</button>
                                  <button className="btn-icon delete" title="Eliminar" onClick={() => setResultatToDelete(resultat)}>🗑️</button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  </table>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </div>

      <ResultatAprenentatgeModal
        isOpen={modalOpen}
        resultat={resultatToEdit}
        onClose={() => { setModalOpen(false); setResultatToEdit(null); }}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={!!resultatToDelete}
        title="Eliminar resultat d'aprenentatge"
        message={`Estàs segur que vols eliminar el RA "${resultatToDelete?.codi} - ${resultatToDelete?.nom}"? Aquesta acció no es pot desfer.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setResultatToDelete(null)}
      />
    </div>
  );
}

export default ModulDetailPage;
